// Helpers for converting between the human-readable mention text shown while
// composing a comment (e.g. "@Anouk Henfling") and the stored token form that
// the activity timeline renders into pills (e.g. "@{a7f5d822-...}").
//
// Keeping the stored form identical to what TicketTimeline parses means no
// backend or renderer change is needed — the editor simply displays names
// while typing, and we translate to/from tokens at the edges (submit / load).
//
// Recipients are always driven by the separately-tracked `mentionedUserIds`
// array, never by parsing this text, so any name that fails to tokenize just
// degrades to plain text — it never produces a wrong recipient.

/** Same UUID-in-braces token shape that TicketTimeline.renderCommentWithMentions matches. */
const TOKEN_RE = /@\{([0-9a-f-]{36})\}/g;

/** Right boundary for a mention: end of string, whitespace, or common punctuation. */
const BOUNDARY = '(?=$|[\\s.,;:!?)\\]}])';

/** Escape a literal string for safe use inside a RegExp. */
export function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Extract the user IDs already encoded as @{uuid} tokens in stored comment text. */
export function extractMentionedIds(storedText: string): string[] {
  const ids: string[] = [];
  for (const m of storedText.matchAll(TOKEN_RE)) {
    ids.push(m[1]);
  }
  return [...new Set(ids)];
}

/**
 * Storage -> Display. Replace each `@{uuid}` token with `@` + display name so an
 * editor shows readable names instead of raw IDs. Unknown IDs fall back to
 * "Unknown User" (mirrors the timeline renderer).
 */
export function toDisplayComment(storedText: string, userMap: Map<string, string>): string {
  return storedText.replace(TOKEN_RE, (_full, uuid: string) => `@${userMap.get(uuid) ?? 'Unknown User'}`);
}

/**
 * Display -> Storage. Replace each mentioned user's `@DisplayName` with its
 * `@{uuid}` token so the timeline can render pills.
 *
 * - Whole-mention match only (right boundary required) so "@Anouk" never matches
 *   inside "@Anoukette".
 * - Longest names first so "@Anouk Henfling" is consumed before a shorter "@Anouk".
 * - Display names are regex-escaped.
 * - IDs whose display name is missing from `userMap` are skipped (degrade to text).
 */
export function buildStorageComment(
  displayText: string,
  mentionedUserIds: string[],
  userMap: Map<string, string>,
): string {
  const entries = [...new Set(mentionedUserIds)]
    .map((id) => ({ id, name: userMap.get(id) }))
    .filter((e): e is { id: string; name: string } => !!e.name)
    .sort((a, b) => b.name.length - a.name.length);

  let result = displayText;
  for (const { id, name } of entries) {
    const re = new RegExp(`@${escapeRegExp(name)}${BOUNDARY}`, 'g');
    result = result.replace(re, `@{${id}}`);
  }
  return result;
}

/**
 * Returns the subset of `mentionedUserIds` whose `@DisplayName` still appears in
 * the editor text — useful to avoid notifying someone whose mention was removed
 * before posting. (Optional; callers may also send the raw list unchanged.)
 */
export function resolveMentionedIds(
  displayText: string,
  mentionedUserIds: string[],
  userMap: Map<string, string>,
): string[] {
  return [...new Set(mentionedUserIds)].filter((id) => {
    const name = userMap.get(id);
    if (!name) return false;
    return new RegExp(`@${escapeRegExp(name)}${BOUNDARY}`).test(displayText);
  });
}
